import { motion } from 'framer-motion';
import { MessageSquare, Zap, Rocket } from 'lucide-react';

export const HowItWorks = () => {
  const steps = [
    {
      number: '01',
      icon: MessageSquare,
      title: 'Add AssistlyAI on WhatsApp',
      description: 'Click the button, scan the QR code, and you\'re connected. Takes less than 30 seconds.',
      color: 'bg-[#25D366]',
    },
    {
      number: '02',
      icon: Zap,
      title: 'Start Chatting Naturally',
      description: 'Just send a message like "Remind me to call mom at 7pm" or "What\'s my schedule tomorrow?"',
      color: 'bg-blue-500',
    },
    {
      number: '03',
      icon: Rocket,
      title: 'Get Things Done',
      description: 'AssistlyAI handles the rest. Your meetings scheduled, reminders set, notes summarised.',
      color: 'bg-purple-500',
    },
  ];

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <section id="how-it-works" className="py-24 lg:py-32" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#25D366] font-semibold text-sm uppercase tracking-wider">How It Works</span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white">
            Three steps to never missing
            <br />
            <span className="text-slate-500 dark:text-slate-400">anything important again.</span>
          </h2>
        </motion.div>

        {/* Steps */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8 lg:gap-12"
        >
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <motion.div key={index} variants={item} className="relative">
                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-1/2 w-full h-0.5 bg-gradient-to-r from-slate-200 dark:from-slate-700 to-transparent z-0" />
                )}
                
                <div className="relative z-10 text-center">
                  {/* Step Number */}
                  <div className="inline-flex items-center justify-center mb-6">
                    <div className={`${step.color} w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  {/* Number Badge */}
                  <div className="mb-4">
                    <span className="text-6xl font-extrabold text-slate-100 dark:text-slate-800">
                      {step.number}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 leading-relaxed max-w-sm mx-auto">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <a
            href="https://wa.me/919981361957?text=Hi%20AssistlyAI!"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-green-500/30"
            data-testid="how-it-works-cta"
          >
            Get Started in 3 Minutes
          </a>
        </motion.div>
      </div>
    </section>
  );
};
